import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import BaseModal from '../common/BaseModal';
import reklamacjeService from '../../services/reklamacjeService';

/**
 * EmailConfigModal — konfiguracja konta e-mail ODDZIALU (per location) dla wysylki reklamacji.
 *
 * Wysylka reklamacji do kontrahenta idzie Z KONTA ODDZIALU (nie z centralnego skrzynki
 * systemowej), dzieki czemu kopia trafia do folderu "Wyslane" tego konta, a odpowiedzi
 * kontrahenta wracaja bezposrednio na skrzynke oddzialu.
 *
 * Transport SMTP (dziala teraz): konto Gmail/inne z haslem aplikacji (app-password),
 * smtp.gmail.com:587 STARTTLS. Gmail OAuth to szkielet na przyszlosc (opcja wylaczona).
 *
 * Sekrety (haslo SMTP) NIGDY nie sa zwracane przez backend — pole hasla jest zawsze
 * puste przy otwarciu; puste = "bez zmiany". Flaga `ma_haslo` mowi czy haslo juz zapisano.
 *
 * Props:
 *   isOpen: bool
 *   onClose(): zamkniecie
 *   locationId: int (wymagany) — oddzial ktorego konfiguracje edytujemy
 *   locationName: string (opcjonalnie, do naglowka)
 *   onSaved(): opcjonalny callback po udanym zapisie
 *
 * Uzywa reklamacjeService.getEmailConfig(locationId) / saveEmailConfig(locationId, data).
 */

const TRANSPORT_SMTP = 'smtp';
const TRANSPORT_GMAIL = 'gmail_oauth';

const DEFAULT_STATE = {
  transport: TRANSPORT_SMTP,
  from_email: '',
  from_nazwa: '',
  smtp_host: 'smtp.gmail.com',
  smtp_port: 587,
  smtp_user: '',
  smtp_haslo: '',
  aktywny: false
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailConfigModal = ({ isOpen, onClose, locationId, locationName, onSaved }) => {
  const [form, setForm] = useState(DEFAULT_STATE);
  const [maHaslo, setMaHaslo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setForm(DEFAULT_STATE);
    setMaHaslo(false);
  }, []);

  // Wczytanie istniejacej konfiguracji przy otwarciu
  useEffect(() => {
    if (!isOpen || !locationId) return;
    let cancelled = false;

    setLoading(true);
    resetForm();
    (async () => {
      try {
        const res = await reklamacjeService.getEmailConfig(locationId);
        if (cancelled) return;
        const cfg = res?.data || {};
        setForm({
          transport: cfg.transport || TRANSPORT_SMTP,
          from_email: cfg.from_email || '',
          from_nazwa: cfg.from_nazwa || '',
          smtp_host: cfg.smtp_host || 'smtp.gmail.com',
          smtp_port: cfg.smtp_port != null ? Number(cfg.smtp_port) : 587,
          smtp_user: cfg.smtp_user || '',
          smtp_haslo: '', // sekret nigdy nie wraca — zawsze puste
          aktywny: !!cfg.aktywny
        });
        setMaHaslo(!!cfg.ma_haslo);
      } catch (err) {
        if (cancelled) return;
        // Brak wpisu (404) = pierwszy raz konfigurujemy ten oddzial — cichy fallback do defaultow.
        // Realne bledy pokazujemy, ale nie blokujemy formularza.
        const msg = err?.message || '';
        if (!/404|nie znaleziono|not found|brak/i.test(msg)) {
          toast.error(msg || 'Nie udalo sie wczytac konfiguracji e-mail');
        }
        resetForm();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, locationId, resetForm]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const fromEmail = (form.from_email || '').trim();
    if (!fromEmail || !EMAIL_RE.test(fromEmail)) {
      toast.error('Podaj poprawny adres e-mail nadawcy (oddzialu)');
      return;
    }

    if (form.transport === TRANSPORT_GMAIL) {
      toast.error('Transport Gmail OAuth nie jest jeszcze dostepny — uzyj SMTP (haslo aplikacji)');
      return;
    }

    if (form.transport === TRANSPORT_SMTP) {
      if (!(form.smtp_host || '').trim()) {
        toast.error('Podaj serwer SMTP (np. smtp.gmail.com)');
        return;
      }
      const port = Number(form.smtp_port);
      if (!port || port < 1 || port > 65535) {
        toast.error('Port SMTP jest nieprawidlowy (typowo 587)');
        return;
      }
      // Przy aktywacji bez wczesniej zapisanego hasla wymagamy app-password
      if (form.aktywny && !maHaslo && !(form.smtp_haslo || '').trim()) {
        toast.error('Aby aktywowac wysylke podaj haslo aplikacji (app-password) konta oddzialu');
        return;
      }
    }

    const payload = {
      transport: form.transport,
      from_email: fromEmail,
      from_nazwa: (form.from_nazwa || '').trim(),
      smtp_host: (form.smtp_host || '').trim(),
      smtp_port: Number(form.smtp_port) || 587,
      // domyslnie login SMTP = adres nadawcy, jesli nie podano osobnego
      smtp_user: (form.smtp_user || '').trim() || fromEmail,
      aktywny: form.aktywny ? 1 : 0
    };
    // Sekret wysylamy TYLKO gdy user cos wpisal (puste = bez zmiany na backendzie)
    const haslo = (form.smtp_haslo || '').trim();
    if (haslo) payload.smtp_haslo = haslo;

    setSaving(true);
    try {
      await reklamacjeService.saveEmailConfig(locationId, payload);
      toast.success('Konfiguracja e-mail oddzialu zapisana');
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      toast.error(err?.message || 'Blad zapisu konfiguracji e-mail');
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    marginBottom: '4px',
    color: 'var(--text-muted)'
  };
  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    backgroundColor: 'var(--card-bg)',
    color: 'var(--text)',
    fontSize: '12px'
  };
  const fieldWrap = { marginBottom: '12px' };

  const isSmtp = form.transport === TRANSPORT_SMTP;

  const footer = (
    <>
      <button
        onClick={onClose}
        disabled={saving}
        style={{
          padding: '6px 12px',
          fontSize: '12px',
          border: '1px solid var(--border)',
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text)',
          borderRadius: '6px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: 500
        }}
      >
        Anuluj
      </button>
      <button
        onClick={handleSave}
        disabled={saving || loading}
        style={{
          padding: '6px 12px',
          fontSize: '12px',
          border: 'none',
          backgroundColor: 'var(--success)',
          color: '#fff',
          borderRadius: '6px',
          cursor: (saving || loading) ? 'not-allowed' : 'pointer',
          fontWeight: 600
        }}
      >
        <i className="fas fa-save" style={{ marginRight: '6px' }} />
        {saving ? 'Zapisywanie...' : 'Zapisz konfiguracje'}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Konfiguracja e-mail oddzialu"
      subtitle={locationName || (locationId ? `Lokalizacja #${locationId}` : '')}
      headerIcon={<i className="fas fa-envelope" />}
      size="md"
      footer={footer}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '12px' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
          Ladowanie konfiguracji...
        </div>
      ) : (
        <>
          {/* Info: skad idzie wysylka */}
          <div
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              padding: '8px 10px',
              marginBottom: '14px',
              fontSize: '11px',
              color: 'var(--text-muted)',
              lineHeight: 1.5
            }}
          >
            <i className="fas fa-info-circle" style={{ marginRight: '6px', color: 'var(--info)' }} />
            Wysylka reklamacji idzie <strong>z konta e-mail tego oddzialu</strong>. Kopia
            wiadomosci trafia do folderu <strong>Wyslane</strong> tej skrzynki, a odpowiedzi
            kontrahenta wracaja bezposrednio na konto oddzialu.
          </div>

          {/* Transport */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Sposob wysylki</label>
            <select
              value={form.transport}
              onChange={(e) => update('transport', e.target.value)}
              style={inputStyle}
            >
              <option value={TRANSPORT_SMTP}>SMTP (haslo aplikacji)</option>
              <option value={TRANSPORT_GMAIL} disabled>
                Gmail OAuth (wkrotce — niedostepne)
              </option>
            </select>
          </div>

          {/* Nadawca */}
          <div style={fieldWrap}>
            <label style={labelStyle}>Adres e-mail nadawcy (oddzialu) *</label>
            <input
              type="email"
              value={form.from_email}
              onChange={(e) => update('from_email', e.target.value)}
              placeholder="reklamacje.oddzial@firma.pl"
              autoComplete="off"
              style={inputStyle}
            />
          </div>

          <div style={fieldWrap}>
            <label style={labelStyle}>Nazwa nadawcy (opcjonalnie)</label>
            <input
              type="text"
              value={form.from_nazwa}
              onChange={(e) => update('from_nazwa', e.target.value)}
              placeholder="np. Sklep Warszawa — Reklamacje"
              style={inputStyle}
            />
          </div>

          {isSmtp && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: '12px' }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Serwer SMTP</label>
                  <input
                    type="text"
                    value={form.smtp_host}
                    onChange={(e) => update('smtp_host', e.target.value)}
                    placeholder="smtp.gmail.com"
                    style={inputStyle}
                  />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Port</label>
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={form.smtp_port}
                    onChange={(e) => update('smtp_port', e.target.value)}
                    placeholder="587"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>Login SMTP (opcjonalnie — domyslnie adres nadawcy)</label>
                <input
                  type="text"
                  value={form.smtp_user}
                  onChange={(e) => update('smtp_user', e.target.value)}
                  placeholder={form.from_email || 'konto@firma.pl'}
                  autoComplete="off"
                  style={inputStyle}
                />
              </div>

              <div style={fieldWrap}>
                <label style={labelStyle}>
                  Haslo aplikacji (app-password)
                  {maHaslo && (
                    <span style={{ color: 'var(--success)', fontWeight: 500, marginLeft: '6px' }}>
                      <i className="fas fa-check-circle" style={{ marginRight: '3px' }} />
                      zapisane
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={form.smtp_haslo}
                  onChange={(e) => update('smtp_haslo', e.target.value)}
                  placeholder={maHaslo ? 'Pozostaw puste — bez zmiany' : 'Wklej haslo aplikacji'}
                  autoComplete="new-password"
                  style={inputStyle}
                />
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.5 }}>
                  Dla Gmaila wygeneruj <strong>haslo aplikacji</strong> (Konto Google →
                  Bezpieczenstwo → Weryfikacja dwuetapowa → Hasla do aplikacji). Zwykle haslo
                  do konta nie zadziala. Haslo jest szyfrowane i nigdy nie jest odczytywane
                  z powrotem.
                </div>
              </div>
            </>
          )}

          {form.transport === TRANSPORT_GMAIL && (
            <div
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--warning, var(--border))',
                borderRadius: '6px',
                padding: '8px 10px',
                marginBottom: '12px',
                fontSize: '11px',
                color: 'var(--text-muted)',
                lineHeight: 1.5
              }}
            >
              <i className="fas fa-triangle-exclamation" style={{ marginRight: '6px', color: 'var(--warning, var(--text-muted))' }} />
              Integracja Gmail OAuth jest w przygotowaniu. Na teraz uzyj transportu
              <strong> SMTP</strong> z haslem aplikacji.
            </div>
          )}

          {/* Aktywny */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '12px',
              color: 'var(--text)',
              marginTop: '4px'
            }}
          >
            <input
              type="checkbox"
              checked={form.aktywny}
              onChange={(e) => update('aktywny', e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>
              <strong>Konfiguracja aktywna</strong>
              <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>
                — bez zaznaczenia wysylka reklamacji z tego oddzialu bedzie zablokowana.
              </span>
            </span>
          </label>
        </>
      )}
    </BaseModal>
  );
};

export default EmailConfigModal;
